import { cardTypeToCardIconId } from '@csc/dls'

export { BigCommerceToStandardAddress, EmptyStandardAddress } from './BigCommerceToStandardAddress'
import {
  formatMoney,
  mapStateToAcronym,
  mapStateToFullName,
  calculatePriceAfterPromoCodeAndDTP,
  calculateTotalDiscountFromLineItems,
} from '@/helpers'

import {
  shipmentsWording,
  shippingOptionsWording,
  BOPUS_SHIPPING_DESCRIPTION,
  FREE_SHIPPING_DESCRIPTION,
  STANDARD_PLUS_SHIPPING_DESCRIPTION,
} from '@/data'

import {
  getCartFromResponse,
} from '@/services/FramedCheckout/cartServices'

import Product from '@/services/Product'

import { FEATURE_TOGGLES, BOPUS_FIELD } from '@/services/Configuration'
import { getCookieByName } from '@/helpers/common'
import { arrivalWindow } from '@/components/Pdp/AddProductToCartForm/EstimatedArrival'
import { error } from 'jquery'

const isLoyaltyCookieEnabled = () => {
  try {
    const rewardsEnabled = getCookieByName('enableRewards') === 'true'
    return rewardsEnabled
  } catch (e) {
    error(e)
  }
  return false
}

const isLoyaltyProgramEnabled = FEATURE_TOGGLES?.isLoyaltyProgramEnabled

const handleTildedField = (value) => value.split('~').shift()
const capitalizeTokens = (value) => value.split(' ').map((token) => `${token[0].toUpperCase()}${token.slice(1).toLowerCase()}`).join(' ')

/**
 * Standardize address returned from Parky, which uses the V2 version of BigC
 * address API, into the schema defined in csc-checkout, common/address.json
 * https://developer.bigcommerce.com/api-reference/store-management/customers-v2/customer-addresses/getallcustomeraddresses
 */

const swapAndDeleteProp = (obj = {}, a, b) => {
  if (obj[a] !== undefined) {
    [obj[a], obj[b]] = [obj[b], obj[a]] // Swap values
    delete obj[a]
  }
}
const standardizeAddressForBigC = (address) => {
  swapAndDeleteProp(address, 'street1', 'address1')
  swapAndDeleteProp(address, 'street2', 'address2')
  swapAndDeleteProp(address, 'stateAcronym', 'stateOrProvinceCode')
  swapAndDeleteProp(address, 'zip', 'postalCode')
  swapAndDeleteProp(address, 'stateName', 'stateOrProvince')
  return JSON.parse(JSON.stringify({
    country: 'United States', countryCode: 'US', shouldSaveAddress: false, ...address,
  }))
}

const standardizeAddressParky = ({
  firstName: first_name,
  lastName: last_name,
  stateAcronym: state_or_province,
  zip: postal_code,
  street1: address1,
  street2: address2,
  customer_id,
  ...rest
}) => ({
  first_name,
  last_name,
  state_or_province: mapStateToFullName(state_or_province),
  postal_code,
  address1,
  address2,
  customer_id,
  ...rest,
})

const standardizeAddress = ({
  address_type: addressType,
  country_iso2: countryCode,
  customer_id: customerId,
  first_name: firstName,
  last_name: lastName,
  state: stateName,
  state_or_province,
  street_1: street1,
  street_2: street2,
  address1,
  address2,
  zip,
  postal_code,
  ...rest
}) => ({
  addressType,
  countryCode,
  customerId,
  firstName,
  lastName,
  stateName: (stateName || state_or_province || ''),
  stateAcronym: mapStateToAcronym(stateName || state_or_province || ''),
  street1: (street1 || address1 || ''),
  street2: (street2 || address2 || ''),
  zip: (zip || postal_code || ''),
  ...rest,
})

/**
 * Standardize the address returned from storefront checkout API
 * which contains a different schema. This works for billingAddress as well
 * as shippingAddress
 * https://developer.bigcommerce.com/api-reference/storefront/checkouts/checkout/checkoutsbycheckoutidget
 * {
 *  billingAddress: {},
 *  consignments: [{
 *    shippingAddress
 *  }]
 * }
 * This will be standardized into the common/address.json schema defined
 * in csc-checkout. Please double check.
 *
 */
const standardizeAddressStorefrontCheckoutApi = ({
  // renamed
  street_1,
  address1 = street_1,
  street2,
  address2 = street2,
  state,
  stateOrProvince = state,
  stateName,
  stateOrProvinceCode: stateAcronym,
  postalCode: zip,
  first_name,
  firstName = first_name,
  last_name,
  lastName = last_name,
  // passed in
  ...rest
}) => ({
  firstName,
  lastName,
  addressType: 'residential', // temporary value, fix me
  street1: address1,
  street2: address2,
  stateName: mapStateToFullName(stateName) || mapStateToFullName(state) || mapStateToFullName(stateOrProvince),
  stateAcronym: mapStateToAcronym(stateAcronym) || mapStateToAcronym(stateName) || mapStateToAcronym(state) || mapStateToAcronym(stateOrProvince),
  zip,
  ...rest,
})

/**
 * Yet another standardizer for a different set of address returned from API
 */
const standardizeAddressSavedInstrumentsApi = ({
  address1: street1,
  address2: street2,
  postalCode: zip,
  stateOrProvince: stateName,
  ...rest
}) => ({
  street1,
  street2,
  zip,
  stateName,
  stateAcronym: mapStateToAcronym(stateName),
  ...rest,
})

const standardizeCart = ({
  lineItems,
  baseAmount,
  cartAmount,
  coupons,
  customerId,
  discountAmount,
  email,
  id,
  updatedTime,
}) => ({
  lineItems: lineItems.map(standardizeLineItem),
  subtotal: baseAmount,
  total: cartAmount,
  discount: discountAmount,
  customerId,
  email,
  id,
  updatedTime,
})

const standardizeCustomer = ({
  addresses,
  company,
  customer_group_id: customerGroupId,
  date_created: createdAt,
  date_modified: updatedAt,
  email,
  firstName,
  id,
  lastName,
  phone,
  taxExemptCategory,
  store_credit_amounts: storeCreditAmounts,
}) => ({
  addresses: addresses.map(standardizeAddress),
  company,
  customerGroupId,
  createdAt,
  updatedAt,
  email,
  firstName,
  id,
  lastName,
  phone,
  taxExemptCategory,
  storeCreditAmounts,
  isLoyaltyCustomer: (
    (isLoyaltyCookieEnabled() || !!isLoyaltyProgramEnabled)
    && !!window?.shoptoken?.isCustomerInLoyaltyProgram?.()
  ),
})

const standardizeProductName = (name, options = []) => `${handleTildedField(name)} ${options.length ? `- ${options?.map((option) => capitalizeTokens(handleTildedField(option.value))).join(', ')}` : ''}`

const standardizeProductOption = ({
  name,
  value,
}) => ({
  name,
  value: capitalizeTokens(handleTildedField(value)),
})

const standardizeLineItem = ({
  name,
  options,
  quantity,
  discounts,
  extendedListPrice,
  ...rest
}) => ({
  name: standardizeProductName(name, options),
  originalName: name,
  options: options?.map(standardizeProductOption),
  salePriceAfterDiscount: calculatePriceAfterPromoCodeAndDTP({
    extendedListPrice,
    quantity,
    discounts,
  }),
  extendedListPrice,
  quantity,
  discounts,
  ...rest,
})

const mapConsignmentIsBopus = (consignment) => {
  const hasTrailingUnderscore = !!(consignment?.shippingAddress?.address2 || '').match(/_$/gi)
  const hasBopusShippingOption = consignment.availableShippingOptions.some((opt) => (
    opt.description === BOPUS_SHIPPING_DESCRIPTION
  ))
  const hasBopusCustomField = !!(
    consignment.shippingAddress?.formFields
    || consignment.shippingAddress?.customFields
  )?.find(({ fieldId, fieldValue }) => fieldId === BOPUS_FIELD && fieldValue === '1')

  const isBopus = (hasTrailingUnderscore || hasBopusShippingOption || hasBopusCustomField)

  return { ...consignment, isBopus }
}

const augmentConsignments = ({ consignments, cart }) => {
  consignments = consignments.map(mapConsignmentIsBopus).sort((a, b) => a.isBopus - b.isBopus)
  const nonBopusShipments = consignments?.filter?.((shipment) => !(shipment.isBopus))
  return consignments.map((consignment, consignmentIndex) => {
    const shipment = {
      ...consignment,
      shippingAddress: standardizeAddressStorefrontCheckoutApi(
        consignment.shippingAddress,
      ),
      lineItems: !!cart?.lineItems && !!consignment?.lineItemIds ? (
        [
          ...cart?.lineItems.filter(({ id }) => consignment?.lineItemIds.indexOf(id) >= 0),
        ].map(({ availabilityMessage, actualOverrideMessage, ...rest }) => ({
          availabilityMessage: consignment.isBopus ? actualOverrideMessage : availabilityMessage,
          actualOverrideMessage,
          ...rest,
        }))
      )
        : [],
    }

    shipment.isFurniture = shipment.isFurniture || !!shipment?.lineItems?.find(({ isFurniture }) => !!isFurniture)

    const shipmentWordingForThisShipment = shipmentsWording[
      shipment.isBopus ? 'isBopus'
        : shipment.isFurniture ? 'isFurniture'
          : 'accessory'
    ]

    Object.assign(
      shipment,
      shipmentWordingForThisShipment,
    )
    if (!shipment.isBopus && consignmentIndex === 0) {
      shipment.showShipmentTitleOnCartEditModal = true
      shipment.showCartEditTitleOnCartEditModal = true
    }

    if (!shipment.isBopus) {
      shipment.shipmentTitle = `SHIPMENT ${nonBopusShipments?.findIndex?.((s) => s.id === shipment.id) + 1} OF ${nonBopusShipments?.length} - ${shipment.shipmentTitle}`
    }

    const optionsWording = shippingOptionsWording
    const standardizeShippingOption = (option) => {
      Object.assign(
        option,
        shipment.isBopus ? optionsWording.isBopus[option.description]
          : shipment.isFurniture ? optionsWording.isFurniture[option.description]
            : optionsWording.accessory[option.description],
      )
      option.isRecommended = option.isRecommended || option.description === BOPUS_SHIPPING_DESCRIPTION

      option.priceRendered = option.cost == 0
        ? 'FREE'
        : option.isRecommended
          ? formatMoney(option.cost)
          : `+ ${formatMoney(option.cost - recommendedShippingOption.cost)}`
      return option
    }
    const recommendedShippingOption = shipment.availableShippingOptions.find((option) => option.isRecommended)
    const hasFreShippingOption = (options) => !!options.find(({ description }) => description === FREE_SHIPPING_DESCRIPTION)
    const freeShippingOptions = [FREE_SHIPPING_DESCRIPTION, STANDARD_PLUS_SHIPPING_DESCRIPTION]

    const filterAvailableShippingOptions = (options) => (
      shipment.isBopus ? options.filter(({ description }) => description === BOPUS_SHIPPING_DESCRIPTION)
        : hasFreShippingOption(options)
          ? options.filter((option) => freeShippingOptions.includes(option.description))
          : options
    )

    const standardizeShippingOptions = (options) => filterAvailableShippingOptions(options).map((option) => standardizeShippingOption(option, recommendedShippingOption))
    shipment.availableShippingOptions = standardizeShippingOptions(shipment.availableShippingOptions)
    return shipment
  })
}


function standardizeOrder({
  consignments,
  billingAddress,
  coupons,
  grandTotal,
  taxTotal,
  shippingCostBeforeDiscount,
  shippingCostTotal,
  orderId,
  status = 'INCOMPLETE',
  subtotal,
  paymentMethod = {},
  customerId,
  email,
  isStoreCreditApplied = false,
  outstandingBalance = 0,
  totalDiscount,
  completedAt,
}, cart) {
  const shipments = augmentConsignments({ consignments, cart })
  const discountFromCoupons = (coupons?.[0]?.discountedAmount || 0)

  const storeCreditsOverflow = 0
  const storeCreditDiscount = 0

  return {
    totalDiscount: totalDiscount || calculateTotalDiscountFromLineItems(cart?.lineItems),
    shipments,
    pickupOnly: shipments.every((consignment) => consignment.isBopus),
    billingAddress: standardizeAddressStorefrontCheckoutApi(billingAddress),
    coupons,
    grandTotal,
    storeCreditDiscount,
    storeCreditsOverflow,
    subtotal,
    taxTotal,
    cart,
    shippingCostBeforeDiscount,
    shippingCostTotal,
    orderId,
    status,
    paymentMethod,
    customerId,
    email,
    isStoreCreditApplied: !!isStoreCreditApplied,
    discountFromCoupons,
    outstandingBalance,
    completedAt,
  }
}

const standardizeOrderComplete = ({
  orderId,
  email,
  customerId,
  customerCanBeCreated,
  billingAddress,
  coupons,
  orderAmount,
  baseAmount,
  taxTotal,
  shippingCostBeforeDiscount,
  shippingCostTotal,
  status,
  payments,
}, lineItems, orderDate = '') => ({
  orderId,
  orderDate,
  email,
  customerId,
  customerCanBeCreated,
  totalDiscount: calculateTotalDiscountFromLineItems(lineItems),
  lineItems: lineItems.map(standardizeLineItem),
  billingAddress: standardizeAddressSavedInstrumentsApi(billingAddress),
  coupons,
  grandTotal: orderAmount,
  subtotal: baseAmount,
  taxTotal,
  shippingCostBeforeDiscount,
  shippingCostTotal,
  status,
  payments,
})

const standardizeOrderFromResponse = ({ bigcResponse, order }) => {
  const cart = getCartFromResponse(bigcResponse?.cart, order)
  return standardizeOrder(bigcResponse, cart)
}

const standardizeCardFromCscPayments = ({
  meta,
  paymentMethodId,
  ...rest
}, index) => {
  const now = new Date()

  const { year, month } = { year: parseInt(meta?.expiration?.year || 0), month: parseInt(meta?.expiration?.month || 0) }
  return {
    card_type: cardTypeToCardIconId(meta?.type || ''),
    address: {
      city: meta?.billTo?.city,
      country: 'United States of America',
      countryCode: 'US',
      firstName: meta?.billTo?.firstName,
      lastName: meta?.billTo?.lastName,
      stateAcronym: meta?.billTo?.state,
      stateName: mapStateToFullName(meta?.billTo?.state),
      street1: meta?.billTo?.street1,
      street2: meta?.billTo?.street2 || '',
      zip: meta?.billTo?.postalCode,
    },
    default: index === 0,
    expiration_date: `${meta?.expiration?.month}/${meta?.expiration?.year}`,
    expired: (
      (parseInt(year) < now.getFullYear()) ||
      (parseInt(year) <= now.getFullYear() && parseInt(month) < now.getMonth())
    ),
    masked_number: meta?.last4 || "**************",
    token: paymentMethodId,
    cardDataFromCSCPayments: { meta, paymentMethodId, ...rest },
  }
}

// Dear future reader, I have to make it like this because there's 12 entrypoints
// for `order.cart.lineItems` on I/O from frame. Attempting to do this non-intrusively
// so we can delete this when we have time to actually use the standardizers properly.
const skuToArrivalCache = {} // This is because spaghetti code will cause calls of this function
const uniqueStrings = (arr) => Object.keys(arr.reduce((h, s) => {
  h[s] = true
  return h
}, {}))
// and it would be vastly superior to not-perform the requests if they're already in the cache.
const MUTATIVELY_appendArrivalInformationToOrderCartAfterStandardizing = async ({ order }) => {
  const lineItems = order?.cart?.lineItems
  if (!lineItems?.length) {
    return order
  }
  // full cache hit. We only care about full hits for simplicity, since the API
  // is N-skus, and marginal added time to request is not high (the round trip is).
  if (lineItems.every(({ sku }) => skuToArrivalCache[sku] != null)) {
    lineItems.forEach((lineItem) => {
      // Again, sorry future reader...
      lineItem.arrivalWindow = lineItem.isDigital ? '' : skuToArrivalCache[lineItem.sku]
    })
  } else {
    // not a full hit. Have to make round trip anyway, might as well do true-result for all.
    const withCompleteContext = await Product.bySkus(uniqueStrings(lineItems.map(({ sku }) => sku)))
    // SHOULD be in-order, but if there are missing items, parallel iteration by index fails.
    withCompleteContext.forEach((product) => {
      const matchedLineItem = lineItems
        .find(({ sku: lineItemSku }) => product
          .variants.some((variant) => variant.sku === lineItemSku))
      const variant = product.warranty.variants[matchedLineItem?.sku] // caution for missing errors
      if (variant) {
        const arrivalWindowMessaging = product.warranty.additional.is_digital ? '' : arrivalWindow({ variant })
        matchedLineItem.arrivalWindow = arrivalWindowMessaging
        skuToArrivalCache[matchedLineItem.sku] = arrivalWindowMessaging
      }
    })
  }
  return order
}

export {
  MUTATIVELY_appendArrivalInformationToOrderCartAfterStandardizing,
  standardizeAddressSavedInstrumentsApi,
  standardizeAddress,
  standardizeAddressForBigC,
  standardizeCart,
  standardizeCustomer,
  standardizeLineItem,
  standardizeProductOption,
  standardizeOrder,
  standardizeOrderComplete,
  standardizeAddressParky,
  standardizeOrderFromResponse,
  standardizeCardFromCscPayments,
}
